// Hospital-themed seed data for CR Heart Center mock.
// Pure data — consumed by mockApi.ts and dashboard pages directly.

const CLIENT_HOSPITAL = '11111111-1111-1111-1111-111111111111'

const THAI_FIRST = [
  'สมชาย',
  'สมหญิง',
  'อารีย์',
  'มานี',
  'วิชัย',
  'พรทิพย์',
  'นภา',
  'ชัยวัฒน์',
  'สุดารัตน์',
  'ปิยะ',
  'ธนพล',
  'นิภา',
  'ประยุทธ์',
  'ศิริพร',
  'อนันต์',
  'จันทรา',
  'ภานุพงศ์',
  'พิมพ์ใจ',
  'กิตติศักดิ์',
  'รุ่งทิวา',
  'อนุชา',
  'สุภาพร',
  'วีระศักดิ์',
  'อรทัย',
  'ปรีชา',
]
const THAI_LAST = [
  'ใจดี',
  'รักเรียน',
  'พิทักษ์ชัย',
  'ศรีวงศ์',
  'ทองใส',
  'แก้วใส',
  'ภักดี',
  'มั่งมี',
  'พรหมจักร',
  'ทิพย์สมบัติ',
  'อนุสรณ์',
  'ไชยวงศ์',
  'พงษ์สุข',
]
const SERVICE_GROUPS = ['Cardiology', 'Health Check', 'Cosmetic', 'Dental', 'General']
const SERVICES_BY_GROUP: Record<string, string[]> = {
  Cardiology: ['Echo Stress Test', 'Cardiac MRI', 'Holter Monitor', 'CABG Consultation'],
  'Health Check': ['Basic Package', 'Premium Package', 'Executive Annual'],
  Cosmetic: ['Botox Forehead', 'Filler Cheek', 'Laser Pigmentation'],
  Dental: ['Cleaning', 'Whitening', 'Root Canal'],
  General: ['Walk-in Consult', 'Vaccination', 'Lab Test'],
}
const PROVIDERS = ['นพ.วิชัย ใจดี', 'พญ.อรทัย ศรีวงศ์', 'นพ.ชัยวัฒน์ พงษ์สุข', 'พญ.พิมพ์ใจ แก้วใส']
const LEAD_SOURCES = [
  'facebook_ads',
  'facebook_messenger',
  'google',
  'tiktok_ads',
  'line_oa',
  'instagram',
  'referral',
  'walk_in',
] as const
const LEAD_STAGES = [
  'intake',
  'qualified',
  'potential',
  'appointed',
  'converted',
  'lost',
  'spam',
] as const

function rng(seed: number) {
  let s = seed
  return () => {
    s = (s * 9301 + 49297) % 233280
    return s / 233280
  }
}
const r = rng(42) // deterministic so reload shows same data

function pick<T>(arr: readonly T[]): T {
  return arr[Math.floor(r() * arr.length)]!
}
function int(min: number, max: number) {
  return Math.floor(r() * (max - min + 1)) + min
}
function daysAgo(d: number) {
  const date = new Date()
  date.setDate(date.getDate() - d)
  return date.toISOString()
}
function uuid(prefix: string, n: number) {
  return `${prefix}-${String(n).padStart(8, '0')}`
}

// ─── Patients (HN) ──────────────────────────────────────────────────
export const MOCK_PATIENTS = Array.from({ length: 24 }, (_, i) => {
  const first = pick(THAI_FIRST)
  const last = pick(THAI_LAST)
  const acquiredDays = int(5, 600)
  const visits = int(0, 12)
  const lastVisitDays = visits > 0 ? int(0, Math.min(acquiredDays, 90)) : null
  const sources = ['lead', 'walk_in', 'referral', 'import', 'manual'] as const
  const statuses = ['active', 'active', 'active', 'inactive'] as const
  return {
    id: uuid('pat', i + 1),
    client_id: CLIENT_HOSPITAL,
    cid: `13${String(int(1000000000, 9999999999)).padStart(10, '0')}`,
    hn: `HN${String(20240000 + i + 1)}`,
    full_name: `${first} ${last}`,
    display_name: first,
    national_id: null,
    date_of_birth: `${int(1955, 2005)}-${String(int(1, 12)).padStart(2, '0')}-${String(int(1, 28)).padStart(2, '0')}`,
    gender: r() > 0.5 ? 'male' : 'female',
    phone: `08${int(10000000, 99999999)}`,
    phone_alt: null,
    email: r() > 0.7 ? `${first.toLowerCase()}@example.com` : null,
    line_id: r() > 0.6 ? `@${first.toLowerCase()}${int(10, 99)}` : null,
    address_line: null,
    district: null,
    province: 'กรุงเทพมหานคร',
    postal_code: null,
    blood_type: pick(['A', 'B', 'AB', 'O', null]),
    allergies: r() > 0.85 ? 'Penicillin' : null,
    chronic_conditions: r() > 0.8 ? 'Hypertension' : null,
    insurance_provider:
      r() > 0.5 ? pick(['Bangkok Insurance', 'AIA', 'BUPA', 'social-security']) : null,
    insurance_id: null,
    source: pick(sources),
    source_lead_id: null,
    acquired_at: daysAgo(acquiredDays),
    status: pick(statuses),
    first_visit_at: visits > 0 ? daysAgo(acquiredDays) : null,
    last_visit_at: lastVisitDays !== null ? daysAgo(lastVisitDays) : null,
    visit_count: visits,
    consent_marketing: r() > 0.3,
    consent_data_sharing: r() > 0.2,
    consent_at: daysAgo(acquiredDays),
    tags: r() > 0.7 ? ['VIP'] : r() > 0.5 ? ['returning'] : null,
    note: null,
    created_at: daysAgo(acquiredDays),
    updated_at: daysAgo(int(0, 30)),
    created_by_user_id: null,
  }
})

// ─── Visits (VN) ─────────────────────────────────────────────────────
export const MOCK_VISITS = Array.from({ length: 38 }, (_, i) => {
  const patient = MOCK_PATIENTS[int(0, MOCK_PATIENTS.length - 1)]!
  const group = pick(SERVICE_GROUPS)
  const service = pick(SERVICES_BY_GROUP[group]!)
  const daysBack = int(0, 90)
  const amount = int(800, 85000)
  const statuses = [
    'completed',
    'completed',
    'completed',
    'scheduled',
    'no_show',
    'cancelled',
  ] as const
  const types = ['first_visit', 'follow_up', 'walk_in', 'consultation', 'procedure'] as const
  return {
    id: uuid('vis', i + 1),
    client_id: CLIENT_HOSPITAL,
    patient_id: patient.id,
    lead_id: null,
    visit_date: daysAgo(daysBack),
    service_group: group,
    service,
    amount: amount.toFixed(2),
    status: pick(statuses),
    visit_type: pick(types),
    lead_source: pick([...LEAD_SOURCES, null]),
    provider: pick(PROVIDERS),
    note: null,
    created_at: daysAgo(daysBack),
    updated_at: daysAgo(daysBack),
    patient: {
      id: patient.id,
      cid: patient.cid,
      hn: patient.hn,
      full_name: patient.full_name,
      phone: patient.phone,
      lifetime_value: (patient.visit_count * int(2000, 20000)).toFixed(2),
    },
  }
})

export const MOCK_REVENUE_TOTAL = MOCK_VISITS.filter((v) => v.status === 'completed')
  .reduce((sum, v) => sum + parseFloat(v.amount), 0)
  .toFixed(2)

// ─── Leads ───────────────────────────────────────────────────────────
export const MOCK_LEADS = Array.from({ length: 32 }, (_, i) => {
  const first = pick(THAI_FIRST)
  const last = pick(THAI_LAST)
  const stage = pick(LEAD_STAGES)
  const source = pick(LEAD_SOURCES)
  const daysBack = int(0, 60)
  const ad_id =
    source.includes('facebook') || source === 'tiktok_ads' || source === 'google'
      ? `ad_${int(100000, 999999)}`
      : null
  return {
    id: uuid('lead', i + 1),
    client_id: CLIENT_HOSPITAL,
    contacted_at: r() > 0.4 ? daysAgo(int(0, daysBack)) : null,
    fb_name: `${first} ${last}`,
    phone: r() > 0.25 ? `08${int(10000000, 99999999)}` : null,
    psid: source.includes('facebook') ? String(int(10000000000000, 99999999999999)) : null,
    source,
    service: pick(['ตรวจหัวใจ', 'แพ็กเกจตรวจสุขภาพ', 'ปรึกษาเรื่อง CABG', 'Botox', null]),
    lead_stage: stage,
    fb_stage: null,
    ad_id,
    ad_journey: ad_id ? pick(['Awareness', 'Consideration', 'Conversion']) : null,
    chat_summary: r() > 0.5 ? 'สนใจแพ็กเกจตรวจสุขภาพ ขอข้อมูลเพิ่ม' : null,
    lost_reason: stage === 'lost' ? pick(['ราคาสูงเกินไป', 'ไปคลินิกอื่น', 'ไม่ติดต่อกลับ']) : null,
    note: null,
    customer_cid: null,
    customer_linked: stage === 'converted',
    created_at: daysAgo(daysBack),
    updated_at: daysAgo(int(0, daysBack)),
  }
})

const stageCounts: Record<string, number> = {
  intake: 0,
  qualified: 0,
  potential: 0,
  appointed: 0,
  converted: 0,
  lost: 0,
  spam: 0,
}
MOCK_LEADS.forEach((l) => {
  stageCounts[l.lead_stage] = (stageCounts[l.lead_stage] ?? 0) + 1
})

export const MOCK_LEAD_STATS = {
  total: MOCK_LEADS.length,
  no_phone_count: MOCK_LEADS.filter((l) => !l.phone).length,
  by_stage: stageCounts as Record<(typeof LEAD_STAGES)[number], number>,
  qualified_plus:
    stageCounts.qualified! +
    stageCounts.potential! +
    stageCounts.appointed! +
    stageCounts.converted!,
  lost_spam: stageCounts.lost! + stageCounts.spam!,
  conversion: stageCounts.converted! / MOCK_LEADS.length,
}

// ─── Content Center: Ad creatives + Tracking links ───────────────────
export interface AdCreative {
  id: string
  ad_id: string
  name: string
  channel: 'facebook' | 'google' | 'tiktok' | 'line'
  thumbnail_color: string
  copy: string
  spend: number
  impressions: number
  clicks: number
  conversions: number
  ctr: number
  cpa: number
  status: 'active' | 'paused' | 'ended'
  created_at: string
}

const AD_PALETTE = ['#1687d9', '#8d58c7', '#db3d91', '#23a06d', '#e08e3c', '#5c5cdc']

export const MOCK_AD_CREATIVES: AdCreative[] = Array.from({ length: 28 }, (_, i) => {
  const channel = pick(['facebook', 'google', 'tiktok', 'line'] as const)
  const impressions = int(8000, 240000)
  const clicks = Math.round(impressions * (r() * 0.04 + 0.005))
  const conversions = Math.round(clicks * (r() * 0.15 + 0.02))
  const spend = int(2000, 80000)
  return {
    id: uuid('cre', i + 1),
    ad_id: `ad_${int(100000, 999999)}`,
    name:
      pick([
        'แพ็กเกจตรวจหัวใจ — โปรเดือน',
        'CR Heart Center — ตรวจคุณก่อนสาย',
        'Songkran Health Promo 2026',
        'สิทธิพิเศษสำหรับสมาชิก LINE',
        'ตรวจ Echo + Treadmill — ลด 30%',
        'ปรึกษาแพทย์ฟรี ไม่มีค่าใช้จ่าย',
      ]) + ` (#${i + 1})`,
    channel,
    thumbnail_color: AD_PALETTE[i % AD_PALETTE.length]!,
    copy: 'ดูแลหัวใจคุณกับทีมแพทย์ผู้เชี่ยวชาญที่ CR Heart Center จองคิวออนไลน์ได้เลยวันนี้',
    spend,
    impressions,
    clicks,
    conversions,
    ctr: clicks / impressions,
    cpa: conversions > 0 ? spend / conversions : 0,
    status: pick(['active', 'active', 'active', 'paused', 'ended'] as const),
    created_at: daysAgo(int(1, 90)),
  }
})

export interface TrackingLink {
  id: string
  short_code: string
  url: string
  campaign: string
  channel: string
  scans: number
  unique_scans: number
  conversions: number
  created_at: string
}

export const MOCK_TRACKING_LINKS: TrackingLink[] = Array.from({ length: 8 }, (_, i) => {
  const channels = ['facebook', 'google', 'line', 'tiktok', 'instagram', 'qr-print']
  const channel = channels[i % channels.length]!
  const scans = int(120, 4500)
  return {
    id: uuid('trk', i + 1),
    short_code: `${channel.slice(0, 3).toUpperCase()}-${int(1000, 9999)}`,
    url: `https://track.bon8.co/${channel}/promo-${i + 1}`,
    campaign: pick([
      'Songkran Promo',
      'Mother Day Care',
      'Annual Health 2026',
      'Cardiac Awareness',
      'Premium Package Q2',
      'Family Day',
    ]),
    channel,
    scans,
    unique_scans: Math.round(scans * 0.78),
    conversions: int(5, 90),
    created_at: daysAgo(int(1, 60)),
  }
})

// ─── Time-series for Executive Summary ───────────────────────────────
export const MOCK_DAILY_SERIES = Array.from({ length: 30 }, (_, i) => {
  const day = 29 - i
  const d = new Date()
  d.setDate(d.getDate() - day)
  return {
    date: d.toISOString().slice(5, 10), // MM-DD
    visits: int(8, 28),
    leads: int(4, 18),
    revenue: int(35000, 180000),
    media_spend: int(8000, 45000),
    new_patients: int(1, 8),
  }
})

// ─── Marketing tab data ──────────────────────────────────────────────
export const MOCK_CHAT_INTENTS = [
  { intent: 'สอบถามแพ็กเกจ', count: 142, change: +18 },
  { intent: 'จองคิว', count: 96, change: +5 },
  { intent: 'ถามราคา', count: 78, change: -3 },
  { intent: 'ตำแหน่งโรงพยาบาล', count: 54, change: +1 },
  { intent: 'ผลตรวจ / นัดติดตาม', count: 41, change: +9 },
  { intent: 'ปรึกษาแพทย์', count: 33, change: -2 },
]

export const MOCK_CHANNEL_BREAKDOWN = [
  { channel: 'Facebook Ads', leads: 124, conv: 18, spend: 142000 },
  { channel: 'LINE OA', leads: 88, conv: 22, spend: 28000 },
  { channel: 'Google', leads: 47, conv: 11, spend: 65000 },
  { channel: 'TikTok Ads', leads: 36, conv: 4, spend: 32000 },
  { channel: 'Referral', leads: 24, conv: 14, spend: 0 },
]

export interface ChannelStateFlow {
  channel: string
  converted: number
  dropoff: number
  not_qualified: number
  spam: number
}
export const MOCK_CHANNEL_STATE_FLOW: ChannelStateFlow[] = [
  { channel: 'Facebook Ads', converted: 18, dropoff: 52, not_qualified: 38, spam: 16 },
  { channel: 'LINE OA', converted: 22, dropoff: 32, not_qualified: 24, spam: 10 },
  { channel: 'Google', converted: 11, dropoff: 20, not_qualified: 12, spam: 4 },
  { channel: 'TikTok Ads', converted: 4, dropoff: 16, not_qualified: 11, spam: 5 },
  { channel: 'Referral', converted: 14, dropoff: 7, not_qualified: 2, spam: 1 },
]

export interface RFMUrgentCustomer {
  hn: string
  name: string
  segment: string
  segment_color: string
  last_visit_days: number
  ltv: number
  reason: string
  action: string
  priority: 'critical' | 'high' | 'medium'
}
export const MOCK_RFM_URGENT_CUSTOMERS: RFMUrgentCustomer[] = [
  {
    hn: 'HN20240012',
    name: 'นายชัยวัฒน์ ภักดี',
    segment: "Can't Lose",
    segment_color: '#db3d91',
    last_visit_days: 95,
    ltv: 84000,
    reason: 'VIP ไม่กลับมา 95 วัน · เคยนัดเดือนละ 2 ครั้ง',
    action: 'โทรหาโดยตรง',
    priority: 'critical',
  },
  {
    hn: 'HN20240008',
    name: 'นางสาวพิมพ์ใจ ทองใส',
    segment: "Can't Lose",
    segment_color: '#db3d91',
    last_visit_days: 112,
    ltv: 72000,
    reason: 'หยุดนัดหลังซื้อ Package ครบ · ยังไม่ต่ออายุ',
    action: 'เสนอ Package ต่อเนื่อง',
    priority: 'critical',
  },
  {
    hn: 'HN20240019',
    name: 'นายวีระศักดิ์ พงษ์สุข',
    segment: 'At Risk',
    segment_color: '#c44569',
    last_visit_days: 78,
    ltv: 56000,
    reason: 'ยกเลิกนัด 2 ครั้งติดกัน · ไม่ตอบ LINE',
    action: 'ส่ง re-engagement offer',
    priority: 'critical',
  },
  {
    hn: 'HN20240003',
    name: 'นางอรทัย ศรีวงศ์',
    segment: 'At Risk',
    segment_color: '#c44569',
    last_visit_days: 65,
    ltv: 48000,
    reason: 'แสดงความไม่พอใจ Admin ใน LINE OA',
    action: 'Satisfaction call',
    priority: 'high',
  },
  {
    hn: 'HN20240015',
    name: 'นายสมชาย ใจดี',
    segment: 'Champions',
    segment_color: '#23a06d',
    last_visit_days: 42,
    ltv: 120000,
    reason: 'รอรับ VIP card > 45 วัน · ยังไม่ได้รับ',
    action: 'จัดส่งด่วน + ขอโทษ',
    priority: 'high',
  },
  {
    hn: 'HN20240021',
    name: 'นางสาวสุดารัตน์ รักเรียน',
    segment: "Can't Lose",
    segment_color: '#db3d91',
    last_visit_days: 88,
    ltv: 64000,
    reason: 'Insurance claim ค้างอยู่ > 60 วัน',
    action: 'ประสาน billing ด่วน',
    priority: 'high',
  },
  {
    hn: 'HN20240007',
    name: 'นายธนพล พรหมจักร',
    segment: 'Need Attention',
    segment_color: '#e0b03c',
    last_visit_days: 55,
    ltv: 32000,
    reason: 'ผลตรวจค้างส่งแพทย์ follow-up > 14 วัน',
    action: 'โทรแจ้งผล + นัดหมาย',
    priority: 'medium',
  },
  {
    hn: 'HN20240024',
    name: 'นางจันทรา ไชยวงศ์',
    segment: 'Loyal Customers',
    segment_color: '#1687d9',
    last_visit_days: 38,
    ltv: 42000,
    reason: 'Package หมดอายุใน 7 วัน · ยังไม่ต่อ',
    action: 'แจ้ง + early renewal',
    priority: 'medium',
  },
]

// Revenue breakdown within each RFM segment by customer LTV tier
// high = top-LTV customers in that segment · mid = middle tier · low = lower tier
// Values are designed to sum to each segment's total revenue in MOCK_RFM_SEGMENTS
export const MOCK_RFM_SEGMENT_VALUE: Record<string, { high: number; mid: number; low: number }> = {
  champions: { high: 2100000, mid: 1000000, low: 320000 },
  loyal: { high: 980000, mid: 840000, low: 360000 },
  potential: { high: 240000, mid: 310000, low: 190000 },
  new: { high: 60000, mid: 120000, low: 100000 },
  promising: { high: 110000, mid: 180000, low: 120000 },
  need_attention: { high: 320000, mid: 380000, low: 190000 },
  about_to_sleep: { high: 140000, mid: 230000, low: 150000 },
  cant_lose: { high: 500000, mid: 310000, low: 110000 },
  at_risk: { high: 380000, mid: 560000, low: 340000 },
  hibernating: { high: 60000, mid: 150000, low: 150000 },
  lost: { high: 15000, mid: 40000, low: 40000 },
}

// ─── RFM segments (taxonomy from real-world clinic analysis) ─────────
export interface RFMSegment {
  key: string
  label: string
  description: string
  action: string
  count: number
  revenue: number
  color: string
}

export const MOCK_RFM_SEGMENTS: RFMSegment[] = [
  {
    key: 'champions',
    label: 'Champions',
    description: 'ซื้อถี่ + ยอดสูง + เพิ่งมาเร็ว',
    action: 'ทำ VIP/Referral/แพ็กเกจต่อเนื่อง',
    count: 87,
    revenue: 3420000,
    color: '#23a06d',
  },
  {
    key: 'loyal',
    label: 'Loyal Customers',
    description: 'ซื้อถี่และมูลค่าดี',
    action: 'Cross-sell / Retention offer',
    count: 142,
    revenue: 2180000,
    color: '#1687d9',
  },
  {
    key: 'potential',
    label: 'Potential Loyalists',
    description: 'เพิ่งมาเร็วและเริ่มมีความถี่',
    action: 'ดูแล follow-up ให้กลับมาซ้ำ',
    count: 96,
    revenue: 740000,
    color: '#8d58c7',
  },
  {
    key: 'new',
    label: 'New Customers',
    description: 'เพิ่งซื้อครั้งแรกในเดือนล่าสุด',
    action: 'Onboarding + next appointment',
    count: 64,
    revenue: 280000,
    color: '#3aa4f0',
  },
  {
    key: 'promising',
    label: 'Promising',
    description: 'เพิ่งมาล่าสุดแต่ความถี่ยังต่ำ',
    action: 'Remarketing เบาๆ / educate',
    count: 78,
    revenue: 410000,
    color: '#5fb0d6',
  },
  {
    key: 'need_attention',
    label: 'Need Attention',
    description: 'เริ่มห่าง แต่มีความถี่พอสมควร',
    action: 'โทร / Line follow-up แบบ personalized',
    count: 53,
    revenue: 890000,
    color: '#e0b03c',
  },
  {
    key: 'about_to_sleep',
    label: 'About to Sleep',
    description: 'เริ่มเงียบและความถี่ไม่สูง',
    action: 'Win-back offer',
    count: 68,
    revenue: 520000,
    color: '#e08e3c',
  },
  {
    key: 'cant_lose',
    label: "At Risk (Can't Lose)",
    description: 'ห่างแต่เคยซื้อถี่มาก',
    action: 'Call back โดยทีมขาย / แพทย์',
    count: 28,
    revenue: 920000,
    color: '#db3d91',
  },
  {
    key: 'at_risk',
    label: 'At Risk',
    description: 'ห่างและเคยซื้อซ้ำ',
    action: 'Retention campaign',
    count: 73,
    revenue: 1280000,
    color: '#c44569',
  },
  {
    key: 'hibernating',
    label: 'Hibernating',
    description: 'ห่างและซื้อไม่ถี่',
    action: 'Low-cost reactivation',
    count: 184,
    revenue: 360000,
    color: '#9a9a9a',
  },
  {
    key: 'lost',
    label: 'Lost',
    description: 'ตกเงื่อนไขอื่นหรือข้อมูลไม่พอ',
    action: 'ตรวจสอบข้อมูล / แยก campaign ต่ำ',
    count: 41,
    revenue: 95000,
    color: '#777777',
  },
]

// 5x5 RFM heatmap — recency 1-5 × frequency 1-5
export const MOCK_RFM_HEATMAP: number[][] = [
  // r=1 (oldest) ... r=5 (most recent)
  [42, 28, 15, 8, 4], // f=1
  [38, 51, 33, 18, 9],
  [22, 44, 67, 41, 18],
  [12, 26, 53, 78, 38],
  [5, 14, 31, 64, 95], // f=5 (most frequent)
]

// ─── Admin Performance ───────────────────────────────────────────────
export const MOCK_STAGE_PIPELINE = [
  { stage: 'Intake', count: 248, drop_pct: 0, avg_days: 0.4 },
  { stage: 'Qualified', count: 156, drop_pct: 37.1, avg_days: 1.2 },
  { stage: 'Potential', count: 102, drop_pct: 34.6, avg_days: 2.8 },
  { stage: 'Appointed', count: 74, drop_pct: 27.5, avg_days: 4.5 },
  { stage: 'Converted', count: 48, drop_pct: 35.1, avg_days: 7.1 },
]

export const MOCK_LOST_REASONS = [
  { reason: 'ราคาสูงเกินไป', count: 38, color: '#db3d91' },
  { reason: 'ไปคลินิกอื่น', count: 27, color: '#e08e3c' },
  { reason: 'ไม่ติดต่อกลับ', count: 22, color: '#9a9a9a' },
  { reason: 'ยังไม่พร้อม', count: 18, color: '#5fb0d6' },
  { reason: 'ระยะทาง / สาขา', count: 9, color: '#23a06d' },
  { reason: 'อื่นๆ', count: 6, color: '#777777' },
]

// Response time by channel — the key Admin metric the user wants
export interface ChannelResponse {
  channel: 'Messenger' | 'LINE OA' | 'TikTok' | 'Instagram' | 'Phone'
  total_chats: number
  avg_first_response_min: number
  median_response_min: number
  within_5min_pct: number
  conv_rate_pct: number
  color: string
  icon: string
}

export const MOCK_CHANNEL_RESPONSE: ChannelResponse[] = [
  {
    channel: 'Messenger',
    total_chats: 312,
    avg_first_response_min: 4.2,
    median_response_min: 2.1,
    within_5min_pct: 72,
    conv_rate_pct: 14.4,
    color: '#1877F2',
    icon: '💬',
  },
  {
    channel: 'LINE OA',
    total_chats: 248,
    avg_first_response_min: 6.8,
    median_response_min: 3.4,
    within_5min_pct: 58,
    conv_rate_pct: 18.1,
    color: '#06C755',
    icon: '📱',
  },
  {
    channel: 'TikTok',
    total_chats: 86,
    avg_first_response_min: 18.3,
    median_response_min: 11.2,
    within_5min_pct: 21,
    conv_rate_pct: 6.8,
    color: '#000000',
    icon: '🎵',
  },
  {
    channel: 'Instagram',
    total_chats: 64,
    avg_first_response_min: 9.5,
    median_response_min: 4.8,
    within_5min_pct: 44,
    conv_rate_pct: 11.2,
    color: '#E1306C',
    icon: '📷',
  },
  {
    channel: 'Phone',
    total_chats: 42,
    avg_first_response_min: 0.8,
    median_response_min: 0.5,
    within_5min_pct: 95,
    conv_rate_pct: 28.4,
    color: '#5c5cdc',
    icon: '☎️',
  },
]

// Response speed bucket → conversion rate (the classic correlation)
export const MOCK_RESPONSE_BUCKETS = [
  { bucket: '< 1 min', leads: 142, conv_rate: 32 },
  { bucket: '1–5 min', leads: 218, conv_rate: 24 },
  { bucket: '5–15 min', leads: 184, conv_rate: 16 },
  { bucket: '15–60 min', leads: 94, conv_rate: 9 },
  { bucket: '> 1 hour', leads: 62, conv_rate: 4 },
]

export const MOCK_QUALITY_METRICS = {
  show_rate: { value: 78, baseline: 75, label: 'Appointment Show Rate', hint: 'นัดแล้วมาจริง' },
  reengagement_rate: {
    value: 14,
    baseline: 10,
    label: 'Re-engagement Rate',
    hint: 'Lost → กลับมาใหม่',
  },
  followup_complete: {
    value: 86,
    baseline: 80,
    label: 'Follow-up Complete',
    hint: 'Lead ถูก follow ครบทุกขั้น',
  },
  upsell_rate: {
    value: 23,
    baseline: 18,
    label: 'Upsell Rate',
    hint: 'Converted → service ซ้ำ/เพิ่ม',
  },
}

export interface AdminMember {
  name: string
  avatar_url: string
  avatar_color: string
  leads_handled: number
  avg_response_min: number
  conv_rate_pct: number
  workload: 'high' | 'normal' | 'low'
}

export const MOCK_ADMIN_TEAM: AdminMember[] = [
  {
    name: 'นัท (Nat)',
    avatar_url: 'https://randomuser.me/api/portraits/women/52.jpg',
    avatar_color: '#db3d91',
    leads_handled: 142,
    avg_response_min: 3.2,
    conv_rate_pct: 22.5,
    workload: 'high',
  },
  {
    name: 'ปลาย (Plai)',
    avatar_url: 'https://randomuser.me/api/portraits/women/33.jpg',
    avatar_color: '#1687d9',
    leads_handled: 118,
    avg_response_min: 4.8,
    conv_rate_pct: 18.6,
    workload: 'normal',
  },
  {
    name: 'อ้อย (Aoy)',
    avatar_url: 'https://randomuser.me/api/portraits/women/58.jpg',
    avatar_color: '#8d58c7',
    leads_handled: 96,
    avg_response_min: 6.4,
    conv_rate_pct: 16.8,
    workload: 'normal',
  },
  {
    name: 'ตูน (Toon)',
    avatar_url: 'https://randomuser.me/api/portraits/women/47.jpg',
    avatar_color: '#23a06d',
    leads_handled: 74,
    avg_response_min: 5.1,
    conv_rate_pct: 19.3,
    workload: 'normal',
  },
  {
    name: 'จ๋า (Ja)',
    avatar_url: 'https://randomuser.me/api/portraits/women/64.jpg',
    avatar_color: '#e08e3c',
    leads_handled: 52,
    avg_response_min: 9.2,
    conv_rate_pct: 12.4,
    workload: 'low',
  },
]

// ─── Marketing — Creative performance signals (HTML-inspired) ────────
export type CreativeSignal = 'scale' | 'optimize' | 'watch' | 'pause'
export const SIGNAL_META: Record<CreativeSignal, { label: string; color: string; bg: string }> = {
  scale: { label: '🟢 Scale', color: '#3B6D11', bg: '#E8F4D9' },
  optimize: { label: '🔵 Optimize', color: '#185FA5', bg: '#DCEDFB' },
  watch: { label: '🟡 Watch', color: '#854F0B', bg: '#FAEEDA' },
  pause: { label: '🔴 Pause', color: '#A32D2D', bg: '#F8DCDC' },
}

// Augment ad creatives with signal + insight
export interface AdSignal {
  id: string
  signal: CreativeSignal
  insight: string
}
export const MOCK_AD_SIGNALS: AdSignal[] = MOCK_AD_CREATIVES.map((ad, i) => {
  const sigArr: CreativeSignal[] = [
    'scale',
    'scale',
    'optimize',
    'optimize',
    'watch',
    'watch',
    'pause',
  ]
  const signal = sigArr[i % sigArr.length]!
  const insights: Record<CreativeSignal, string> = {
    scale: `CTR สูงและ CPA ต่ำ — เพิ่ม budget +30%`,
    optimize: `Copy ดี แต่ CTR เริ่มลด — ทดลอง variant ใหม่`,
    watch: `Spend สูง conv ปานกลาง — ดูใกล้ชิด`,
    pause: `Conv ต่ำมาก เกิน 14 วัน — แนะนำ pause`,
  }
  return { id: ad.id, signal, insight: insights[signal] }
})

// ─── Marketing — Funnel + cycle (HTML-inspired) ──────────────────────
export const MOCK_MKT_FUNNEL = [
  { stage: 'Reach', count: 248_000, color: '#5c5cdc' },
  { stage: 'Click', count: 12_400, color: '#1687d9' },
  { stage: 'Lead', count: 820, color: '#3aa4f0' },
  { stage: 'Qualified', count: 480, color: '#8d58c7' },
  { stage: 'Appointed', count: 186, color: '#db3d91' },
  { stage: 'Converted', count: 94, color: '#23a06d' },
]

export const MOCK_MKT_CYCLE_BY_SERVICE = [
  { service: 'Cardiology — Echo Stress', avg_days: 8.4 },
  { service: 'Health Check Premium', avg_days: 4.2 },
  { service: 'Cosmetic — Botox', avg_days: 1.8 },
  { service: 'Dental — Whitening', avg_days: 2.6 },
  { service: 'CABG Consultation', avg_days: 14.2 },
]

// ─── Chat Center (raw data from pancake.biz exports) ─────────────────
export type ChatChannel = 'facebook' | 'instagram' | 'line' | 'tiktok'
export type ChatIntent = 'inquiry' | 'booking' | 'complaint' | 'price' | 'follow_up' | 'other'
export type ChatSentiment = 'positive' | 'neutral' | 'negative'

export interface ChatRecord {
  id: string
  date: string
  channel: ChatChannel
  page: string
  customer: string
  agent: string
  intent: ChatIntent
  sentiment: ChatSentiment
  first_response_min: number
  total_messages: number
  duration_min: number
  outcome: 'qualified' | 'lost' | 'pending' | 'converted'
  ad_id: string | null
  summary: string
}

const PANCAKE_PAGES = ['CR Heart Center', 'CR Heart - LINE OA', 'CRH IG Official']
const AGENTS = ['นัท', 'ปลาย', 'อ้อย', 'ตูน', 'จ๋า']
const SAMPLE_SUMMARIES = [
  'สอบถามแพ็กเกจตรวจหัวใจ Echo + Treadmill',
  'จองคิวปรึกษาแพทย์ที่สาขาสุขุมวิท',
  'ถามราคา CABG + ระยะเวลาพักฟื้น',
  'ขอข้อมูลแพทย์เฉพาะทางและเวลาตรวจ',
  'ติดตามผลตรวจ + นัด follow-up',
  'สอบถามเรื่องประกัน BUPA ครอบคลุมไหม',
]

export const MOCK_CHATS: ChatRecord[] = Array.from({ length: 36 }, (_, i) => {
  const channel = pick(['facebook', 'facebook', 'instagram', 'line', 'line', 'tiktok'] as const)
  const intent = pick(['inquiry', 'booking', 'price', 'follow_up', 'complaint', 'other'] as const)
  const sentiment = pick(['positive', 'positive', 'neutral', 'neutral', 'negative'] as const)
  const outcome = pick(['qualified', 'qualified', 'pending', 'lost', 'converted'] as const)
  const first = int(0, 24) // minutes
  const fname = pick(THAI_FIRST)
  const lname = pick(THAI_LAST)
  const adFromCh = channel === 'facebook' || channel === 'instagram' || channel === 'tiktok'
  return {
    id: uuid('chat', i + 1),
    date: daysAgo(int(0, 14)),
    channel,
    page: pick(PANCAKE_PAGES),
    customer: `${fname} ${lname}`,
    agent: pick(AGENTS),
    intent,
    sentiment,
    first_response_min: first,
    total_messages: int(2, 18),
    duration_min: int(2, 45),
    outcome,
    ad_id: adFromCh && r() > 0.4 ? `ad_${int(100000, 999999)}` : null,
    summary: pick(SAMPLE_SUMMARIES),
  }
})

// ─── Tasks (scheduled jobs / system actions) ─────────────────────────
export type TaskCadence = 'daily' | 'weekly' | 'monthly' | 'once'
export type TaskStatus = 'active' | 'paused' | 'completed' | 'failed'

export interface ScheduledTask {
  id: string
  name: string
  description: string
  cadence: TaskCadence
  next_run: string
  last_run: string | null
  last_status: 'success' | 'failed' | null
  status: TaskStatus
  owner: string
  category: 'sync' | 'report' | 'campaign' | 'cleanup' | 'reminder'
}

const TASK_DATA: Omit<ScheduledTask, 'id' | 'next_run' | 'last_run' | 'last_status'>[] = [
  {
    name: 'Sync FB Lead Forms',
    description: 'ดึง leads ใหม่จาก FB Ads ทุกชั่วโมง',
    cadence: 'daily',
    status: 'active',
    owner: 'system',
    category: 'sync',
  },
  {
    name: 'Daily KPI Email Digest',
    description: 'ส่ง email สรุปยอดวันก่อนหน้าให้ทีม',
    cadence: 'daily',
    status: 'active',
    owner: 'นัท',
    category: 'report',
  },
  {
    name: 'Refresh RFM Segments',
    description: 'อัปเดต RFM segments ทุกวันจันทร์ 6:00',
    cadence: 'weekly',
    status: 'active',
    owner: 'system',
    category: 'sync',
  },
  {
    name: 'Monthly Ad Spend Report',
    description: 'PDF สรุปค่าโฆษณา + ROAS ส่งหัวหน้า',
    cadence: 'monthly',
    status: 'active',
    owner: 'ปลาย',
    category: 'report',
  },
  {
    name: 'Win-back Campaign — Hibernating',
    description: 'ส่ง LINE broadcast หาลูกค้าที่หายไป >120 วัน',
    cadence: 'monthly',
    status: 'paused',
    owner: 'นัท',
    category: 'campaign',
  },
  {
    name: 'Birthday Reminder',
    description: 'ส่งข้อความอวยพร + voucher วันเกิดลูกค้า',
    cadence: 'daily',
    status: 'active',
    owner: 'system',
    category: 'reminder',
  },
  {
    name: 'Cleanup Spam Leads',
    description: 'ลบ leads ที่ stage = spam อายุ >90 วัน',
    cadence: 'weekly',
    status: 'active',
    owner: 'system',
    category: 'cleanup',
  },
  {
    name: 'No-show Follow-up',
    description: 'แจ้งทีมโทรกลับลูกค้าที่ไม่มาตามนัด ภายใน 24 ชม.',
    cadence: 'daily',
    status: 'active',
    owner: 'อ้อย',
    category: 'reminder',
  },
  {
    name: 'Songkran Promo Activation',
    description: 'เปิด tracking link + email ทันที',
    cadence: 'once',
    status: 'completed',
    owner: 'นัท',
    category: 'campaign',
  },
  {
    name: 'Q2 RFM Deep-dive Report',
    description: 'รายงาน Champion drop + at-risk segment',
    cadence: 'once',
    status: 'active',
    owner: 'ปลาย',
    category: 'report',
  },
]

export const MOCK_TASKS: ScheduledTask[] = TASK_DATA.map((t, i) => {
  const nextDays = t.status === 'completed' ? -int(1, 7) : int(0, 14)
  const lastDays = t.status === 'completed' ? int(1, 7) : int(1, 7)
  return {
    id: uuid('task', i + 1),
    ...t,
    next_run: daysAgo(-nextDays), // future
    last_run: t.status === 'active' || t.status === 'completed' ? daysAgo(lastDays) : null,
    last_status: t.status === 'failed' ? 'failed' : t.status === 'paused' ? null : 'success',
  }
})

// ─── Notifications (bell dropdown) ────────────────────────────────────
export type NotificationKind = 'alert' | 'limit' | 'success' | 'info'

export interface Notification {
  id: string
  kind: NotificationKind
  title: string
  body: string
  date: string
  read: boolean
}

export const MOCK_NOTIFICATIONS: Notification[] = [
  {
    id: 'n1',
    kind: 'alert',
    title: 'Response time TikTok สูงผิดปกติ',
    body: 'เฉลี่ย 18 นาที (baseline 5 นาที) — ทีมควรตรวจสอบ',
    date: daysAgo(0),
    read: false,
  },
  {
    id: 'n2',
    kind: 'limit',
    title: 'Ad spend เกือบถึง limit รายเดือน',
    body: 'ใช้ไป ฿142K / 150K (95%) — เหลือ 6 วัน',
    date: daysAgo(0),
    read: false,
  },
  {
    id: 'n3',
    kind: 'success',
    title: 'Daily KPI digest ส่งสำเร็จ',
    body: 'ส่งให้ทีมแล้ว 8 คน เมื่อ 09:00',
    date: daysAgo(0),
    read: false,
  },
  {
    id: 'n4',
    kind: 'alert',
    title: '3 leads ยังไม่ถูก assigned',
    body: 'จาก FB Ads เข้ามา >2 ชม. ที่ผ่านมา',
    date: daysAgo(1),
    read: true,
  },
  {
    id: 'n5',
    kind: 'info',
    title: 'RFM segments อัปเดตเสร็จ',
    body: 'Champions เพิ่ม +4 / At Risk ลด -7',
    date: daysAgo(1),
    read: true,
  },
  {
    id: 'n6',
    kind: 'success',
    title: 'Cleanup Spam Leads — เสร็จแล้ว',
    body: 'ลบ 12 records ที่อายุเกิน 90 วัน',
    date: daysAgo(2),
    read: true,
  },
]

// ─── Content Center — channel-based content items ────────────────────
export type ContentChannel = 'website' | 'facebook' | 'instagram' | 'tiktok' | 'youtube' | 'offline'

export interface ContentItem {
  id: string
  channel: ContentChannel
  name: string
  thumbnail_color: string
  thumbnail_url: string | null
  description: string
  status: 'live' | 'scheduled' | 'paused' | 'draft'
  impressions: number
  clicks: number
  conversions: number
  spend: number
  short_link: string
  created_at: string
  scheduled_at: string | null
  campaign_id?: string
  persona_id?: string | null
  content_pillar_id?: string | null
  funnel_stage?: string | null
  decision_state?: string | null
  source_type?: 'legacy' | 'human_upload' | 'ai_generated'
  content_format?: string | null
  copy_variants?: Record<string, string>
  recommended_variant?: 'A' | 'B' | 'C' | null
  generation_rationale?: string | null
  workflow_status?: 'draft' | 'in_review' | 'approved' | 'revision_requested'
  revision?: number
  exported_at?: string | null
  external_post_id?: string | null
  live_url?: string | null
}

const CHANNEL_NAMES = {
  website: ['Landing — ตรวจหัวใจ', 'Blog post — Echo คืออะไร', 'Article — ดูแลหัวใจ 5 วิธี'],
  facebook: [
    'FB Banner — Songkran Promo',
    'FB Reel — Heart Stress Test',
    'FB Carousel — แพ็กเกจ',
    'FB Video — Doctor Q&A',
    'FB Post — Health Day',
  ],
  instagram: ['IG Story — Patient Testimonial', 'IG Reel — สาธิต Echo', 'IG Carousel — Tips'],
  tiktok: ['TikTok Short — Heart Rate', 'TikTok — เทคนิคดูแลตัวเอง', 'TikTok — Doctor Talk #1'],
  youtube: ['YouTube — สาระสุขภาพหัวใจ EP1', 'YouTube — Live Q&A กับแพทย์'],
  offline: [
    'Billboard BTS Asok',
    'Posters คลินิก สาขาสุขุมวิท',
    'Brochure — ตรวจหัวใจ',
    'QR — Print Counter',
  ],
}

export const MOCK_CONTENT_ITEMS: ContentItem[] = (() => {
  const items: ContentItem[] = []
  let n = 1
  ;(Object.entries(CHANNEL_NAMES) as [ContentChannel, string[]][]).forEach(([ch, names]) => {
    names.forEach((nm, idx) => {
      const impressions = ch === 'offline' ? int(50_000, 800_000) : int(5000, 320_000)
      const clicks = ch === 'offline' ? 0 : Math.round(impressions * (r() * 0.04 + 0.005))
      const conversions = Math.round((clicks || impressions * 0.0008) * (r() * 0.15 + 0.02))
      const spend = ch === 'offline' ? int(20_000, 120_000) : int(3000, 80_000)
      items.push({
        id: uuid('cnt', n++),
        channel: ch,
        name: nm,
        thumbnail_color: AD_PALETTE[(n - 1) % AD_PALETTE.length]!,
        thumbnail_url: `https://picsum.photos/seed/cnt${n}/480/270`,
        description: 'ดูแลหัวใจคุณกับทีมแพทย์ผู้เชี่ยวชาญ จองคิวออนไลน์ได้',
        status: pick(['live', 'live', 'live', 'scheduled', 'paused', 'draft'] as const),
        impressions,
        clicks,
        conversions,
        spend,
        short_link: `https://track.bon8.co/${ch}/${nm
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .slice(0, 24)}-${idx + 1}`,
        created_at: daysAgo(int(1, 90)),
        scheduled_at: r() > 0.7 ? daysAgo(-int(1, 14)) : null,
      })
    })
  })
  return items
})()

export const MOCK_MKT_ACTIONS = [
  {
    priority: 'P1',
    title: 'Pause creative #11 (Family Day) — Conv 0 / spend ฿18K',
    kpi: 'Save ฿35K/mo',
  },
  {
    priority: 'P1',
    title: 'Scale creative #3 (Songkran Promo) — เพิ่ม budget 30%',
    kpi: '+22 leads/mo',
  },
  { priority: 'P2', title: 'Cardiology cycle 8.4 days — เพิ่ม follow-up D3', kpi: '+8% CR' },
  { priority: 'P2', title: 'TikTok response 18 min — ปรับ alert 5 min threshold', kpi: '+6% conv' },
  {
    priority: 'P3',
    title: 'Hibernating segment 184 cust — low-cost reactivation',
    kpi: 'Re-eng +12',
  },
]

// ─── Executive supplementary data ────────────────────────────────────

export interface ServiceAttrib {
  service: string
  revenue: number
  new_patients: number
  returning: number
  attribution: { type: 'fb_ad' | 'tiktok' | 'blog' | 'ig'; label: string; id?: string }[]
}

export const MOCK_SERVICE_ATTRIBUTION: ServiceAttrib[] = [
  {
    service: 'Echo Stress Test',
    revenue: 920000,
    new_patients: 18,
    returning: 10,
    attribution: [
      { type: 'fb_ad', label: 'FB Ad', id: 'FB-A02' },
      { type: 'blog', label: 'Blog: หัวใจ' },
    ],
  },
  {
    service: 'Premium Health',
    revenue: 680000,
    new_patients: 9,
    returning: 13,
    attribution: [
      { type: 'tiktok', label: 'TikTok #health' },
      { type: 'fb_ad', label: 'FB Ad', id: 'FB-A05' },
    ],
  },
  {
    service: 'CABG Consultation',
    revenue: 540000,
    new_patients: 4,
    returning: 8,
    attribution: [
      { type: 'blog', label: 'Blog: CABG' },
      { type: 'ig', label: 'IG Reel' },
    ],
  },
  {
    service: 'Cardiac MRI',
    revenue: 420000,
    new_patients: 7,
    returning: 9,
    attribution: [{ type: 'fb_ad', label: 'FB Ad', id: 'FB-A08' }],
  },
  {
    service: 'Holter Monitor',
    revenue: 280000,
    new_patients: 6,
    returning: 8,
    attribution: [{ type: 'tiktok', label: 'TikTok #holter' }],
  },
]

export interface ContentActivity {
  label: string
  icon: string
  count: number
  delta: number
}
export const MOCK_CONTENT_ACTIVITY: ContentActivity[] = [
  { label: 'Blog / เว็บบทความ', icon: '📝', count: 4, delta: +1 },
  { label: 'Short VDO (Meta)', icon: '🎬', count: 12, delta: +3 },
  { label: 'TikTok Content', icon: '🎵', count: 8, delta: +2 },
  { label: 'Active Ads (Meta)', icon: '📢', count: 23, delta: -2 },
]

export interface ExecAction {
  priority: 'P1' | 'P2' | 'P3'
  category: 'ads' | 'admin' | 'content' | 'rfm'
  title: string
  detail: string
  cta: string
}
export const MOCK_EXEC_ACTIONS: ExecAction[] = [
  {
    priority: 'P1',
    category: 'ads',
    title: 'เพิ่ม budget "Echo Stress Test" (FB-A02)',
    detail: 'New Visit 42 คนใน 7 วัน · ROI 4.8× — Top ROI creative ของเดือน',
    cta: 'ปรับ Budget',
  },
  {
    priority: 'P1',
    category: 'admin',
    title: '10 ราย ตอบลูกค้าช้า > 3 นาที (LINE OA)',
    detail: 'ช่วง 12:00–14:00 น. — ส่งผลให้ conv ลด 18% ในช่วงกลางวัน',
    cta: 'กำชับทีม',
  },
  {
    priority: 'P2',
    category: 'content',
    title: 'เพิ่มบทความ keyword "ตรวจสุขภาพก่อนผ่าตัด"',
    detail: 'กระแสสูงใน Social · คู่แข่ง 2 ราย (ABC Clinic, XYZ Hospital) เริ่มทำแล้ว',
    cta: 'สร้าง Brief',
  },
  {
    priority: 'P2',
    category: 'rfm',
    title: "28 คน (Can't Lose) ไม่กลับมา > 90 วัน",
    detail: 'LTV เฉลี่ย ฿32K/คน — ส่ง retention offer ก่อนหลุดออกจาก segment',
    cta: 'ส่ง Campaign',
  },
  {
    priority: 'P2',
    category: 'ads',
    title: 'Pause creative FB-A11 (Family Day)',
    detail: 'Conv 0 ครั้ง / Spend ฿18K — ต่ำกว่า benchmark 80%',
    cta: 'Pause Ad',
  },
  {
    priority: 'P3',
    category: 'content',
    title: 'Scale TikTok "Echo Stress" เป็น Spark Ad',
    detail: 'Organic views +340% WoW — boost จะช่วยเพิ่ม reach ได้อีก 3–5×',
    cta: 'สร้าง Spark Ad',
  },
]

// ─── Sankey data ─────────────────────────────────────────────────────

export interface SankeyNodeDef {
  id: string
  label: string
  color: string
  sublabel?: string
}
export interface SankeyLinkDef {
  source: string
  target: string
  value: number
  color?: string
}

/** Marketing Funnel Sankey: Lead → stages → Converted + Lost branches */
export const SANKEY_FUNNEL_NODES: SankeyNodeDef[] = [
  { id: 'lead', label: 'Lead', color: '#5c5cdc' },
  { id: 'qualified', label: 'Qualified', color: '#8d58c7' },
  { id: 'appointed', label: 'Appointed', color: '#db3d91' },
  { id: 'converted', label: 'Converted', color: '#23a06d' },
  { id: 'lost_q', label: 'Drop', color: '#cbd5e1' },
  { id: 'lost_a', label: 'Drop', color: '#cbd5e1' },
  { id: 'lost_c', label: 'Drop', color: '#cbd5e1' },
]
export const SANKEY_FUNNEL_LINKS: SankeyLinkDef[] = [
  { source: 'lead', target: 'qualified', value: 480 },
  { source: 'lead', target: 'lost_q', value: 340, color: '#e2e8f0' },
  { source: 'qualified', target: 'appointed', value: 186 },
  { source: 'qualified', target: 'lost_a', value: 294, color: '#e2e8f0' },
  { source: 'appointed', target: 'converted', value: 94 },
  { source: 'appointed', target: 'lost_c', value: 92, color: '#e2e8f0' },
]

/** Stage Pipeline Sankey (Admin tab): CRM stages with drop-off */
export const SANKEY_PIPELINE_NODES: SankeyNodeDef[] = [
  { id: 'intake', label: 'Intake', color: '#5c5cdc' },
  { id: 'sq', label: 'Qualified', color: '#8d58c7' },
  { id: 'potential', label: 'Potential', color: '#3aa4f0' },
  { id: 'sappointed', label: 'Appointed', color: '#db3d91' },
  { id: 'sconverted', label: 'Converted', color: '#23a06d' },
  { id: 'd1', label: 'Drop', color: '#cbd5e1' },
  { id: 'd2', label: 'Drop', color: '#cbd5e1' },
  { id: 'd3', label: 'Drop', color: '#cbd5e1' },
  { id: 'd4', label: 'Drop', color: '#cbd5e1' },
]
export const SANKEY_PIPELINE_LINKS: SankeyLinkDef[] = [
  { source: 'intake', target: 'sq', value: 156 },
  { source: 'intake', target: 'd1', value: 92, color: '#e2e8f0' },
  { source: 'sq', target: 'potential', value: 102 },
  { source: 'sq', target: 'd2', value: 54, color: '#e2e8f0' },
  { source: 'potential', target: 'sappointed', value: 74 },
  { source: 'potential', target: 'd3', value: 28, color: '#e2e8f0' },
  { source: 'sappointed', target: 'sconverted', value: 48 },
  { source: 'sappointed', target: 'd4', value: 26, color: '#e2e8f0' },
]

/** RFM Revenue Distribution Sankey: revenue → segment tier → segment */
export const SANKEY_RFM_NODES: SankeyNodeDef[] = [
  // Tier groupings (col 0)
  { id: 'r_high', label: 'High Value', color: '#23a06d' },
  { id: 'r_mid', label: 'Mid Value', color: '#e0b03c' },
  { id: 'r_low', label: 'Low / At-Risk', color: '#db3d91' },
  // Individual segments (col 1)
  { id: 'champions', label: 'Champions', color: '#23a06d', sublabel: '฿3.4M' },
  { id: 'loyal', label: 'Loyal', color: '#1687d9', sublabel: '฿2.2M' },
  { id: 'potential', label: 'Potential', color: '#8d58c7', sublabel: '฿740K' },
  { id: 'need_attention', label: 'Need Attention', color: '#e0b03c', sublabel: '฿890K' },
  { id: 'cant_lose', label: "Can't Lose", color: '#db3d91', sublabel: '฿920K' },
  { id: 'at_risk', label: 'At Risk', color: '#c44569', sublabel: '฿1.3M' },
  { id: 'others', label: 'Others', color: '#9a9a9a', sublabel: '฿1.3M' },
]
export const SANKEY_RFM_LINKS: SankeyLinkDef[] = [
  { source: 'r_high', target: 'champions', value: 3420000 },
  { source: 'r_high', target: 'loyal', value: 2180000 },
  { source: 'r_mid', target: 'potential', value: 740000 },
  { source: 'r_mid', target: 'need_attention', value: 890000 },
  { source: 'r_low', target: 'cant_lose', value: 920000 },
  { source: 'r_low', target: 'at_risk', value: 1280000 },
  { source: 'r_low', target: 'others', value: 1325000 },
]

// ─── FunnelFlow data ──────────────────────────────────────────────────────────
import type { FunnelStage, FunnelSource, FunnelDropReason } from '../components/charts/FunnelFlow'

export const FUNNEL_LEAD_STAGES: FunnelStage[] = [
  { id: 'lead', label: 'Lead', value: 820, color: '#6D6CF4' },
  { id: 'qualified', label: 'Qualified', value: 480, color: '#A78BFA' },
  { id: 'appointed', label: 'Appointed', value: 196, color: '#F472B6' },
  { id: 'converted', label: 'Converted', value: 94, color: '#34D399' },
]

export const FUNNEL_LEAD_SOURCES: FunnelSource[] = [
  { label: 'SEO', count: 428, pct: '51%' },
  { label: 'Facebook', count: 205, pct: '25%' },
  { label: 'IG', count: 123, pct: '15%' },
  { label: 'Google Ads', count: 57, pct: '7%' },
  { label: 'Unknown', count: 7, pct: '2%' },
]

export const FUNNEL_LEAD_DROP_REASONS: FunnelDropReason[] = [
  { label: 'ราคา', count: 327, pct: '45%' },
  { label: 'ไม่ตอบ', count: 131, pct: '18%' },
  { label: 'ทำไม่ได้', count: 80, pct: '11%' },
  { label: 'วันว่างไม่ตรง', count: 7, pct: '1%' },
  { label: 'อื่น ๆ', count: 181, pct: '25%' },
]

// CRM Stage Flow funnel
export const FUNNEL_CRM_STAGES: FunnelStage[] = [
  { id: 'new', label: 'New Lead', value: 820, color: '#60A5FA' },
  { id: 'contacted', label: 'Contacted', value: 618, color: '#818CF8' },
  { id: 'qualified', label: 'Qualified', value: 480, color: '#A78BFA' },
  { id: 'appointed', label: 'Appointed', value: 196, color: '#F472B6' },
  { id: 'converted', label: 'Converted', value: 94, color: '#34D399' },
]

export const FUNNEL_CRM_SOURCES: FunnelSource[] = [
  { label: 'Inbound Form', count: 312, pct: '38%' },
  { label: 'Facebook Lead', count: 226, pct: '28%' },
  { label: 'LINE OA', count: 148, pct: '18%' },
  { label: 'Walk-in', count: 90, pct: '11%' },
  { label: 'Referral', count: 44, pct: '5%' },
]

export const FUNNEL_CRM_DROP_REASONS: FunnelDropReason[] = [
  { label: 'ราคาสูงเกิน', count: 248, pct: '34%' },
  { label: 'ไม่ตอบสาย', count: 186, pct: '26%' },
  { label: 'เปลี่ยนใจ', count: 142, pct: '19%' },
  { label: 'รอข้อมูลเพิ่ม', count: 96, pct: '13%' },
  { label: 'อื่น ๆ', count: 54, pct: '7%' },
]

// ─── Marketing Performance — SEO / AI Search / Ads ───────────────────

export interface SeoKeyword {
  keyword: string
  position: number
  prev_position: number
  search_volume: number
  url: string
  difficulty: 'low' | 'medium' | 'high'
}

export const MOCK_SEO_KEYWORDS: SeoKeyword[] = [
  {
    keyword: 'รักษาหัวใจ กรุงเทพ',
    position: 2,
    prev_position: 4,
    search_volume: 8400,
    url: '/services/cardiology',
    difficulty: 'high',
  },
  {
    keyword: 'โรงพยาบาลหัวใจ ราคา',
    position: 5,
    prev_position: 7,
    search_volume: 5200,
    url: '/services/cardiology',
    difficulty: 'high',
  },
  {
    keyword: 'ตรวจหัวใจ ราคาถูก',
    position: 3,
    prev_position: 3,
    search_volume: 3900,
    url: '/checkup/heart',
    difficulty: 'medium',
  },
  {
    keyword: 'cardiac screening package',
    position: 8,
    prev_position: 12,
    search_volume: 2100,
    url: '/packages/cardiac',
    difficulty: 'medium',
  },
  {
    keyword: 'ผ่าตัดหัวใจ ค่าใช้จ่าย',
    position: 11,
    prev_position: 9,
    search_volume: 1800,
    url: '/services/surgery',
    difficulty: 'high',
  },
  {
    keyword: 'ปรึกษาแพทย์หัวใจ ออนไลน์',
    position: 6,
    prev_position: 10,
    search_volume: 1400,
    url: '/telemedicine',
    difficulty: 'low',
  },
  {
    keyword: 'heart center bangkok',
    position: 4,
    prev_position: 5,
    search_volume: 1200,
    url: '/',
    difficulty: 'medium',
  },
  {
    keyword: 'นัดหมอหัวใจ ออนไลน์',
    position: 9,
    prev_position: 14,
    search_volume: 960,
    url: '/appointment',
    difficulty: 'low',
  },
  {
    keyword: 'check up หัวใจ package',
    position: 15,
    prev_position: 18,
    search_volume: 740,
    url: '/packages',
    difficulty: 'low',
  },
  {
    keyword: 'เส้นเลือดหัวใจตีบ รักษา',
    position: 22,
    prev_position: 20,
    search_volume: 620,
    url: '/services/angioplasty',
    difficulty: 'high',
  },
]

export interface AiSearchMention {
  platform: string
  icon: string
  monthly_mentions: number
  prev_mentions: number
  sentiment: 'positive' | 'neutral' | 'negative'
  sample_query: string
  rank_in_answer: number | null
}

export const MOCK_AI_SEARCH_MENTIONS: AiSearchMention[] = [
  {
    platform: 'ChatGPT',
    icon: '🤖',
    monthly_mentions: 1240,
    prev_mentions: 840,
    sentiment: 'positive',
    sample_query: 'โรงพยาบาลหัวใจที่ดีที่สุดในไทย',
    rank_in_answer: 2,
  },
  {
    platform: 'Google AI',
    icon: '✨',
    monthly_mentions: 2180,
    prev_mentions: 1620,
    sentiment: 'positive',
    sample_query: 'cardiac hospital bangkok',
    rank_in_answer: 1,
  },
  {
    platform: 'Perplexity',
    icon: '🔍',
    monthly_mentions: 380,
    prev_mentions: 210,
    sentiment: 'neutral',
    sample_query: 'heart surgery cost thailand',
    rank_in_answer: 3,
  },
  {
    platform: 'Claude',
    icon: '💡',
    monthly_mentions: 290,
    prev_mentions: 140,
    sentiment: 'positive',
    sample_query: 'best cardiologist in bangkok',
    rank_in_answer: 2,
  },
  {
    platform: 'Bing Copilot',
    icon: '🪟',
    monthly_mentions: 560,
    prev_mentions: 480,
    sentiment: 'neutral',
    sample_query: 'รักษาหัวใจ ที่ไหนดี',
    rank_in_answer: 4,
  },
]

export interface AdsPlatformPerformance {
  platform: string
  color: string
  impressions: number
  clicks: number
  conversions: number
  spend: number
  ctr: number
  cpc: number
  cpa: number
  roas: number
}

export const MOCK_ADS_PLATFORM_PERF: AdsPlatformPerformance[] = [
  {
    platform: 'Facebook Ads',
    color: '#1877F2',
    impressions: 284000,
    clicks: 7240,
    conversions: 318,
    spend: 62400,
    ctr: 2.55,
    cpc: 8.62,
    cpa: 196,
    roas: 4.1,
  },
  {
    platform: 'Google Ads',
    color: '#EA4335',
    impressions: 156000,
    clicks: 4820,
    conversions: 196,
    spend: 38200,
    ctr: 3.09,
    cpc: 7.93,
    cpa: 195,
    roas: 3.8,
  },
  {
    platform: 'TikTok Ads',
    color: '#000000',
    impressions: 412000,
    clicks: 6180,
    conversions: 142,
    spend: 28400,
    ctr: 1.5,
    cpc: 4.59,
    cpa: 200,
    roas: 2.9,
  },
  {
    platform: 'LINE OA',
    color: '#06C755',
    impressions: 48000,
    clicks: 2640,
    conversions: 114,
    spend: 12000,
    ctr: 5.5,
    cpc: 4.55,
    cpa: 105,
    roas: 5.2,
  },
]

// ─── AI-first platform: AI Queue, Client Status, AI Notifications ─────

export type AIOutputType =
  | 'caption'
  | 'content_plan'
  | 'report'
  | 'creative_idea'
  | 'blog_draft'
  | 'campaign_idea'
  | 'follow_up'

export type AIReviewStatus = 'pending_review' | 'approved' | 'rejected' | 'needs_revision'

export interface AIQueueItem {
  id: string
  client_id: string
  client_name: string
  type: AIOutputType
  title: string
  preview: string
  status: AIReviewStatus
  generated_at: string
  reviewed_at: string | null
  confidence: number
}

const AI_TYPE_LABEL: Record<AIOutputType, string> = {
  caption: 'Caption',
  content_plan: 'Content Plan',
  report: 'Monthly Report',
  creative_idea: 'Creative Concept',
  blog_draft: 'Blog Draft',
  campaign_idea: 'Campaign Idea',
  follow_up: 'Follow-up Sequence',
}

const AI_PREVIEWS: Record<AIOutputType, string[]> = {
  caption: [
    '✨ ดูแลหัวใจคุณก่อนสาย! ด้วย Echo Heart Screening แพ็กเกจพิเศษ ราคา 2,990 บาท รองรับทุกสิทธิ์การรักษา นัดหมายออนไลน์ได้ 24 ชม.',
    '💚 สุขภาพดีเริ่มต้นที่คุณ ตรวจหัวใจเชิงป้องกันกับทีมแพทย์ผู้เชี่ยวชาญ บริการอบอุ่น รอไม่นาน ผลตรวจชัดเจน',
    '📍 LINE OA follow-up draft: "สวัสดีคุณ [ชื่อ] ทางคลินิกอยากนัดตรวจ follow-up ครั้งต่อไปให้กับคุณ มีเวลาว่างช่วงไหนบ้างคะ?"',
  ],
  content_plan: [
    'แผน Content เดือน พ.ค. 2569: 12 posts Facebook, 8 Reels, 4 Blog posts, 3 LINE Broadcast — Theme: สุขภาพเชิงป้องกัน',
    'Content Strategy Q2: กลุ่มเป้าหมาย 35-55 ปี สนใจดูแลสุขภาพเชิงป้องกัน — ช่องทางหลัก: Facebook + LINE OA',
  ],
  report: [
    'รายงานประจำเดือน เม.ย. 2569: Leads ใหม่ 47 ราย (+12% MoM), Conversion 31%, New Patients 15 ราย, Revenue ฿1.24M',
    'Weekly KPI Digest: Ad Spend ฿82,000, New Leads 18 ราย, CTR 3.2%, Cost per Lead ฿4,556',
  ],
  creative_idea: [
    'Concept "Heart Heroes": รูปแพทย์ในธีม superhero พร้อม infographic "5 สัญญาณเตือน" เหมาะ FB Reel + IG Story',
    'Reel Idea: Before & After checkup story — เปิดด้วยคำถาม "รู้ไหมว่าหัวใจคุณแข็งแรงแค่ไหน?" + upbeat BGM',
  ],
  blog_draft: [
    '5 สัญญาณเตือนโรคหัวใจที่คุณอาจมองข้าม: 1. เหนื่อยง่ายกว่าปกติ 2. แน่นหน้าอกเป็นครั้งคราว 3. ใจสั่นโดยไม่มีสาเหตุ...',
    'Echo คืออะไร? ทำไมแพทย์ถึงแนะนำ: การตรวจด้วยคลื่นเสียงความถี่สูง ไม่เจ็บ ไม่มีรังสี เห็นการทำงานของหัวใจแบบ realtime...',
  ],
  campaign_idea: [
    'Campaign "ตรวจก่อนป่วย" 2 สัปดาห์: ช่องทาง FB+LINE, เป้า 50 leads, งบ ฿30,000, CTA: จองฟรี consultation',
    'Referral Program: ลูกค้าเดิมแนะนำเพื่อน รับส่วนลด ฿500 ต่อกันทั้งสองฝ่าย — ส่งผ่าน LINE OA อัตโนมัติ',
  ],
  follow_up: [
    'Follow-up sequence สำหรับ Lead ที่ไม่ได้นัด: D+3 message สอบถาม, D+7 reminder + ข้อเสนอพิเศษ, D+14 final',
    'Re-engagement สำหรับ patient ที่ไม่มา > 6 เดือน: "สวัสดีค่ะ ทางคลินิกห่วงใยสุขภาพของคุณ ตรวจติดตามผลดีไหมคะ?"',
  ],
}

const _AI_CLIENTS = [
  { id: 'client-ch', name: 'CR Heart Center' },
  { id: 'client-tc', name: 'The One Clinic' },
  { id: 'client-kh', name: 'Krungthai Hospital' },
  { id: 'client-tl', name: 'The One Lab' },
  { id: 'client-bs', name: 'BrightSmile Dental' },
  { id: 'client-sm', name: 'Sunrise Medical' },
]

const _AI_TYPES: AIOutputType[] = [
  'caption',
  'content_plan',
  'report',
  'creative_idea',
  'blog_draft',
  'campaign_idea',
  'follow_up',
]

const _AI_STATUSES: AIReviewStatus[] = [
  'pending_review',
  'pending_review',
  'pending_review',
  'pending_review',
  'approved',
  'approved',
  'rejected',
  'needs_revision',
]

export const MOCK_AI_QUEUE: AIQueueItem[] = Array.from({ length: 24 }, (_, i) => {
  const client = _AI_CLIENTS[i % _AI_CLIENTS.length]!
  const type = _AI_TYPES[i % _AI_TYPES.length]!
  const previews = AI_PREVIEWS[type]
  const preview = previews[i % previews.length]!
  const status = _AI_STATUSES[i % _AI_STATUSES.length]!
  return {
    id: `ai-${i + 1}`,
    client_id: client.id,
    client_name: client.name,
    type,
    title: `${AI_TYPE_LABEL[type]} — ${client.name}`,
    preview,
    status,
    generated_at: daysAgo(i < 6 ? 0 : i < 14 ? 1 : 2),
    reviewed_at: status === 'pending_review' ? null : daysAgo(0),
    confidence: 62 + ((i * 11) % 32),
  }
})

// ─── Cross-client status for AE dashboard ─────────────────────────────

export type ClientHealthStatus = 'good' | 'attention' | 'urgent'

export interface ClientStatusCard {
  id: string
  name: string
  industry: 'hospital' | 'clinic' | 'sme'
  pending_reviews: number
  urgent_alerts: number
  new_leads: number
  visits_this_week: number
  health: ClientHealthStatus
  next_action: string
  last_report_days_ago: number
}

export const MOCK_CLIENT_STATUS: ClientStatusCard[] = [
  {
    id: 'client-ch',
    name: 'CR Heart Center',
    industry: 'hospital',
    pending_reviews: 5,
    urgent_alerts: 0,
    new_leads: 12,
    visits_this_week: 8,
    health: 'good',
    next_action: 'Review monthly report draft',
    last_report_days_ago: 7,
  },
  {
    id: 'client-tc',
    name: 'The One Clinic',
    industry: 'clinic',
    pending_reviews: 3,
    urgent_alerts: 1,
    new_leads: 8,
    visits_this_week: 5,
    health: 'attention',
    next_action: 'Handle negative chat alert',
    last_report_days_ago: 5,
  },
  {
    id: 'client-kh',
    name: 'Krungthai Hospital',
    industry: 'hospital',
    pending_reviews: 0,
    urgent_alerts: 2,
    new_leads: 19,
    visits_this_week: 14,
    health: 'urgent',
    next_action: 'Respond to 2 complaint chats',
    last_report_days_ago: 14,
  },
  {
    id: 'client-tl',
    name: 'The One Lab',
    industry: 'clinic',
    pending_reviews: 2,
    urgent_alerts: 0,
    new_leads: 4,
    visits_this_week: 3,
    health: 'good',
    next_action: 'Approve content plan for May',
    last_report_days_ago: 3,
  },
  {
    id: 'client-bs',
    name: 'BrightSmile Dental',
    industry: 'clinic',
    pending_reviews: 7,
    urgent_alerts: 0,
    new_leads: 6,
    visits_this_week: 4,
    health: 'attention',
    next_action: 'Review 7 pending AI outputs',
    last_report_days_ago: 10,
  },
  {
    id: 'client-sm',
    name: 'Sunrise Medical',
    industry: 'hospital',
    pending_reviews: 1,
    urgent_alerts: 0,
    new_leads: 3,
    visits_this_week: 2,
    health: 'good',
    next_action: 'Schedule Q2 campaign brief',
    last_report_days_ago: 2,
  },
]

// ─── AI-specific notifications (extends existing NotificationKind) ─────

export type AINotificationKind = 'ai_complete' | 'ai_failed' | 'review_reminder' | 'urgent_client'

export const MOCK_AI_NOTIFICATIONS: Notification[] = [
  {
    id: 'ain1',
    kind: 'success',
    title: 'AI สร้าง Monthly Report เสร็จแล้ว',
    body: 'CR Heart Center — เม.ย. 2569 พร้อม review',
    date: daysAgo(0),
    read: false,
  },
  {
    id: 'ain2',
    kind: 'alert',
    title: 'Krungthai Hospital — Urgent Alert',
    body: 'พบ 2 negative chats วันนี้ รอการตอบกลับ > 3 ชม.',
    date: daysAgo(0),
    read: false,
  },
  {
    id: 'ain3',
    kind: 'info',
    title: 'AI สร้าง Content Plan พ.ค. เสร็จแล้ว',
    body: 'The One Clinic — 12 posts, 8 Reels พร้อม review',
    date: daysAgo(0),
    read: false,
  },
  {
    id: 'ain4',
    kind: 'limit',
    title: 'มี 7 งาน AI รอ review นานกว่า 24 ชม.',
    body: 'BrightSmile Dental: 4 items, CR Heart Center: 3 items',
    date: daysAgo(1),
    read: false,
  },
  {
    id: 'ain5',
    kind: 'alert',
    title: 'AI Generate Failed — Campaign Idea',
    body: 'The One Lab — ลองใหม่ หรือปรับ prompt ใน Review Center',
    date: daysAgo(1),
    read: true,
  },
  {
    id: 'ain6',
    kind: 'info',
    title: 'Content ที่ approve ถึง schedule พรุ่งนี้',
    body: 'CR Heart Center — FB Banner Songkran จะโพสต์ 10:00 น.',
    date: daysAgo(1),
    read: true,
  },
]
